import isEqual from 'lodash/isEqual';

import {createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : String(value);

const normalizeMapTask = task => {
    if (!isPlainObject(task)) {
        return {
            itemsExpression: '',
            maxConcurrency: '',
            maxConcurrencyExpression: ''
        };
    }

    return {
        ...cloneValue(task),
        itemsExpression: normalizeString(task.itemsExpression),
        maxConcurrency: normalizeString(task.maxConcurrency),
        maxConcurrencyExpression: normalizeString(task.maxConcurrencyExpression)
    };
};

const getResource = (mxResources, key) => mxResources.get(key);

const createDivider = (mxResources, text) => {
    const divider = document.createElement('div');
    divider.className = text ? 'ui dividing header task-concurrency-header' : 'ui horizontal divider';
    divider.textContent = text || getResource(mxResources, 'or');
    return divider;
};

const createMapTaskForm = ({
                               task = {},
                               taskErrors = {},
                               editable = true,
                               mxResources,
                               jsonExpressionHelp = null,
                               onMapChange = () => {}
                           } = {}) => {
    let mapTask = normalizeMapTask(task);

    const container = document.createElement('div');
    container.id = 'mapEditor';
    container.className = 'ui form';

    const itemsExpressionField = createInputField({
        label: getResource(mxResources, 'taskMapItemsExpression'),
        help: getResource(mxResources, 'taskMapItemsExpressionHelp'),
        id: 'itemsExpression',
        name: 'itemsExpression',
        type: 'text',
        className: 'required',
        placeHolder: getResource(mxResources, 'expressionPlaceholder'),
        value: mapTask.itemsExpression,
        error: taskErrors?.fields?.itemsExpression
    });
    const itemsExpressionInput = itemsExpressionField.querySelector('input');
    itemsExpressionInput.addEventListener('change', event => emitMap({itemsExpression: event.target.value}));

    const maxConcurrencyField = createInputField({
        label: getResource(mxResources, 'taskMaxConcurrency'),
        id: 'taskMaxConcurrency',
        name: 'taskMaxConcurrency',
        type: 'number',
        min: 0,
        value: mapTask.maxConcurrency,
        placeHolder: 0,
        error: taskErrors?.fields?.maxConcurrency
    });
    const maxConcurrencyInput = maxConcurrencyField.querySelector('input');
    maxConcurrencyInput.addEventListener('change', event => emitMap({maxConcurrency: event.target.value}));

    const maxConcurrencyExpressionField = createInputField({
        label: getResource(mxResources, 'taskMaxConcurrencyExpression'),
        id: 'taskMaxConcurrencyExpression',
        name: 'taskMaxConcurrencyExpression',
        type: 'text',
        placeHolder: getResource(mxResources, 'expressionPlaceholder'),
        value: mapTask.maxConcurrencyExpression,
        error: taskErrors?.fields?.maxConcurrencyExpression
    });
    const maxConcurrencyExpressionInput = maxConcurrencyExpressionField.querySelector('input');
    maxConcurrencyExpressionInput.addEventListener('change', event => emitMap({maxConcurrencyExpression: event.target.value}));

    container.appendChild(itemsExpressionField);
    container.appendChild(createDivider(mxResources, getResource(mxResources, 'taskConcurrency')));
    container.appendChild(maxConcurrencyField);
    container.appendChild(createDivider(mxResources));
    container.appendChild(maxConcurrencyExpressionField);

    const syncStructuredFields = () => {
        itemsExpressionInput.value = mapTask.itemsExpression;
        maxConcurrencyInput.value = mapTask.maxConcurrency;
        maxConcurrencyExpressionInput.value = mapTask.maxConcurrencyExpression;
    };

    const appendJsonExpressionHelp = help => {
        if (!help || maxConcurrencyExpressionField.querySelector('details')) {
            return;
        }

        maxConcurrencyExpressionField.insertAdjacentHTML('beforeend', help);
    };

    function emitMap(patch) {
        const nextMapTask = normalizeMapTask({
            ...mapTask,
            ...patch
        });

        if (isEqual(mapTask, nextMapTask)) {
            return;
        }

        mapTask = nextMapTask;
        Object.assign(task, patch);
        onMapChange(cloneValue(patch), cloneValue(mapTask));
    }

    if (!editable) {
        disableFormControls(container);
    }

    appendJsonExpressionHelp(jsonExpressionHelp);

    return {
        element: container,
        getTask: () => cloneValue(mapTask),
        setTask: value => {
            mapTask = normalizeMapTask(value);
            Object.assign(task, cloneValue(mapTask));
            syncStructuredFields();
        },
        appendJsonExpressionHelp
    };
};

export {
    createMapTaskForm,
    normalizeMapTask
};
